import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, combineLatest, from, Observable, Subject, Subscription } from 'rxjs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { select, Store } from '@ngrx/store';
import { debounceTime, filter, map, take, takeUntil } from 'rxjs/operators';
import * as reducers from '../../../modules/+sourcing/application/reducers';
import * as coreTypes from '../../../core/types';
import * as actions from '../../actions/sent-emails';
import { LoadPayload } from '../../actions/sent-emails';
import { DataStateChangeEvent, GridDataResult } from '@progress/kendo-angular-grid';
import * as _ from 'lodash';
import { cloneDeep } from '@apollo/client/utilities';
import { AuthResourcePipe } from '../../pipes';
import { CollectionNameEnum, Email, SentEmailsLookup } from '../../models';
import { convertLookupToQueryParams } from '../../../core/helpers';
import { ActivatedRoute, Router } from '@angular/router';
import * as sharedTypes from '../../../shared/types';

export enum PreviewTypeEnum {
  None,
  Email,
  Attachment,
}

@Component({
  selector: 'app-abstract-emails',
  template: '',
})
export abstract class AbstractEmailsComponent implements OnInit, OnDestroy {
  emailList$: Observable<Array<Email>>;
  lookupCriteria$: Observable<SentEmailsLookup>;
  totalItems$: Observable<number>;
  emailById$: Observable<Email>;
  sourcingRequestIdInEmailById: string | null = null;
  emailByIdIsLoading$: Observable<boolean>;
  gridData$: BehaviorSubject<GridDataResult>;
  emailListLoading$: Observable<boolean>;
  bodyPreview$: Observable<SafeHtml>;
  emailBodyIsLoading$: Observable<boolean>;
  gridStateSubscription: Subscription;
  previewTypeEnum = PreviewTypeEnum;
  previewType: PreviewTypeEnum = PreviewTypeEnum.None;
  titlePreview: string;
  selectedAttachmentUrl: string;
  showFilterDialog: boolean = false;
  appliedFiltersCont$: Observable<number>;
  destroy$: Subject<void> = new Subject<void>();
  protected EMAIL_SERVICE_PATH: string;
  protected STUDIO_URL: string;
  protected fieldsToSkip: Array<string> = ['skip', 'total', 'sort', 'limit', 'skipSending'];

  protected abstract getLocalStorageKey(): string;

  protected abstract getLookupFilterCondition(
    lookup: SentEmailsLookup,
    initialLookup: SentEmailsLookup
  ): boolean;

  protected abstract getTabName(): string;

  constructor(
    protected store$: Store<any>,
    protected sanitizer: DomSanitizer,
    protected router: Router,
    protected route: ActivatedRoute,
    protected authResourcePipe: AuthResourcePipe,
    @Inject(coreTypes.SERVICE_TOKEN)
    protected configService: coreTypes.IConfigService,
    @Inject(sharedTypes.NOTIFICATION_SERVICE_TOKEN)
    private notificationService: sharedTypes.INotificationService
  ) {
    this.EMAIL_SERVICE_PATH = this.configService.get('EmailServiceEndpoint');
    this.STUDIO_URL = window.location.origin;
  }

  ngOnInit(): void {
    this.emailListLoading$ = this.store$.pipe(select(reducers.getSentEmailsLoading));
    this.emailBodyIsLoading$ = this.store$.pipe(select(reducers.getEmailBodyLoading));
    this.totalItems$ = this.store$.pipe(select(reducers.getSentEmailsTotalItems));
    this.emailById$ = this.store$.pipe(select(reducers.getEmailById));
    this.emailByIdIsLoading$ = this.store$.pipe(select(reducers.getEmailByIdLoading));
    this.lookupCriteria$ = this.store$.pipe(select(reducers.getSentEmailsLookup));
    this.emailList$ = this.store$.pipe(
      select(reducers.getSentEmailsItems),
      map((emails) => {
        if (!emails.length) {
          return [];
        }
        return [...emails].sort((a, b) => (b.skipSending ? 1 : -1) - (a.skipSending ? 1 : -1));
      })
    );
    const initialLookup = new SentEmailsLookup();
    this.appliedFiltersCont$ = this.lookupCriteria$.pipe(
      map((lookup) => {
        return Object.entries(lookup).reduce((acc, [key, value]) => {
          if (
            !this.fieldsToSkip.includes(key) &&
            !_.isEqual(value ?? null, initialLookup[key] ?? null)
          ) {
            if (key === 'documents' && Array.isArray(value)) {
              acc += value.length;
            } else {
              acc += 1;
            }
          }
          return acc;
        }, 0);
      })
    );
    this.bodyPreview$ = this.store$
      .pipe(select(reducers.getEmailBody))
      .pipe(map((body) => this.sanitizer.bypassSecurityTrustHtml(body)));
    this.gridStateSubscription = combineLatest([this.emailList$, this.totalItems$]).subscribe(
      ([emailList, totalItems]) => {
        this.updateGrid(emailList, totalItems);
      }
    );

    this.lookupCriteria$
      .pipe(
        takeUntil(this.destroy$),
        filter((lookup) => this.getLookupFilterCondition(lookup, initialLookup))
      )
      .subscribe((lookup) => {
        const queryParams = convertLookupToQueryParams<SentEmailsLookup>(lookup, this.fieldsToSkip);
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams,
        });
      });
  }

  protected updateGrid(collection: Array<Email>, total: number): void {
    if (this.gridData$ === undefined) {
      this.gridData$ = new BehaviorSubject<GridDataResult>({
        data: collection,
        total: total,
      });
    } else {
      this.gridData$.next({
        data: collection,
        total: total,
      });
    }
  }

  protected onShowEmailPreview(dataItem: Email): void {
    this.previewType = this.previewTypeEnum.Email;
    this.titlePreview = dataItem.subject;
    this.store$.dispatch(new actions.LoadEmailBodyAction(dataItem.id));
    this.updateEmailIdQueryParam(dataItem.id);
  }

  protected onShowAttachmentPreview({
    dataItem,
    attachmentName,
  }: {
    dataItem: Email;
    attachmentName: string;
  }): void {
    this.previewType = this.previewTypeEnum.Attachment;
    this.selectedAttachmentUrl = `${this.EMAIL_SERVICE_PATH}/email/${dataItem.id}/attachment/${attachmentName}`;
    this.titlePreview = attachmentName;
  }

  protected async onDownloadAttachment({
    dataItem,
    attachmentName,
  }: {
    dataItem: Email;
    attachmentName: string;
  }): Promise<void> {
    this.selectedAttachmentUrl = `${this.EMAIL_SERVICE_PATH}/email/${dataItem.id}/attachment/${attachmentName}`;
    this.authResourcePipe.transform(this.selectedAttachmentUrl, true).then((resourceBlob) => {
      const url = URL.createObjectURL(resourceBlob);

      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = attachmentName;
      anchor.click();
      URL.revokeObjectURL(url);
    });
  }

  protected onFilterDialogOpen(): void {
    this.showFilterDialog = true;
  }

  protected onFilterDialogClose(): void {
    this.showFilterDialog = false;
  }

  protected onLookupChanged(lookup: SentEmailsLookup): void {
    const lookupClone: SentEmailsLookup = cloneDeep(lookup);
    localStorage.setItem(this.getLocalStorageKey(), JSON.stringify(lookupClone));
    lookupClone.skip = 0;
    this.search(lookupClone);
  }

  protected search(lookup: SentEmailsLookup): void {
    this.store$.dispatch(new actions.LoadSentEmailsAction(new LoadPayload(lookup)));
  }

  async onDataStateChange(event: DataStateChangeEvent): Promise<void> {
    const lookup = _.cloneDeep(
      await this.store$.pipe(select(reducers.getSentEmailsLookup), take(1)).toPromise()
    );
    lookup.skip = event.skip;
    lookup.limit = event.take;
    if (!_.isEqual(lookup.sort, event.sort)) {
      lookup.sort = event.sort;
      lookup.skip = 0;
    }
    this.search(lookup);
  }

  protected closePreview(): void {
    this.previewType = this.previewTypeEnum.None;
    this.updateEmailIdQueryParam(null);
  }

  protected updateEmailIdQueryParam(emailId: string | null): void {
    this.lookupCriteria$.pipe(take(1)).subscribe((lookup) => {
      const updatedLookup = new SentEmailsLookup({ ...lookup, id: emailId });
      const queryParams = convertLookupToQueryParams<SentEmailsLookup>(
        updatedLookup,
        this.fieldsToSkip,
        emailId ? [] : ['id']
      );
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams,
        queryParamsHandling: 'merge',
      });
    });
  }

  protected onCopyEmailLink(emailId: string): void {
    this.copyToClipboard(
      `${this.STUDIO_URL}/sourcing/sent-emails/(sent-emails:${this.getTabName()})?id=${emailId}`,
      'Email Link Copied'
    );
  }

  protected copyToClipboard(value: string, message: string): void {
    from(navigator.clipboard.writeText(value))
      .pipe(take(1), debounceTime(100))
      .subscribe(
        () => {
          this.notificationService.show(
            message,
            sharedTypes.NotificationStyle.Bar,
            sharedTypes.NotificationType.Success,
            sharedTypes.NotificationPosition.Top,
            1000
          );
        },
        () => {
          this.notificationService.show(
            'Copy Failed',
            sharedTypes.NotificationStyle.Bar,
            sharedTypes.NotificationType.Danger,
            sharedTypes.NotificationPosition.Top,
            1000
          );
        }
      );
  }

  onEmailDetailsExpend(dataItem: Email): void {
    this.store$.dispatch(new actions.LoadEmailByIdAction(dataItem.id));

    this.emailById$
      .pipe(
        filter((email) => !!email),
        take(1)
      )
      .subscribe((email) => {
        this.determineSourcingRequestId(email);
      });
  }

  private determineSourcingRequestId(email: Email): void {
    if (!email?.document) {
      this.sourcingRequestIdInEmailById = null;
      return;
    }

    const sourcingRequestDocs = email.document.filter(
      (doc: any) => doc.collection === CollectionNameEnum.SOURCING_REQUEST
    );

    this.sourcingRequestIdInEmailById =
      sourcingRequestDocs.length === 1 ? sourcingRequestDocs[0].documentId : null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.gridStateSubscription) {
      this.gridStateSubscription.unsubscribe();
    }
  }
}
